import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  SearchCriteria,
  SearchEntityType,
  LifecycleState
} from '../../models/file-vault.models';

export interface FilterChangeEvent {
  criteria: Partial<SearchCriteria>;
  activeFiltersCount: number;
}

@Component({
  selector: 'fv-search-filters',
  standalone: true,
  template: `
    <div class="search-filters-container" [class.collapsed]="isCollapsed">
      <!-- Header -->
      <div class="filters-header">
        <span class="header-title">Filters</span>
        <div class="header-actions">
          <span class="active-count" *ngIf="activeFiltersCount > 0">
            {{ activeFiltersCount }} active
          </span>
          <button kendoButton [look]="'flat'" (click)="clearAllFilters()" *ngIf="activeFiltersCount > 0">
            Clear All
          </button>
          <button kendoButton [look]="'flat'" (click)="toggleCollapse()" class="collapse-btn">
            {{ isCollapsed ? '▶' : '◀' }}
          </button>
        </div>
      </div>

      <div class="filters-body" *ngIf="!isCollapsed">
        <!-- Entity Type Facets -->
        <div class="filter-section" *ngIf="showEntityTypeFacets">
          <div class="section-header" (click)="toggleSection('entityType')">
            <span>Type</span>
            <span class="toggle-icon">{{ expandedSections['entityType'] ? '−' : '+' }}</span>
          </div>
          <div class="section-content" *ngIf="expandedSections['entityType']">
            <label class="facet-item" *ngFor="let facet of entityTypeFacets">
              <input type="checkbox"
                     [checked]="selectedEntityTypes.has(facet.value)"
                     (change)="toggleEntityType(facet.value)"
                     [disabled]="facet.count === 0" />
              <span class="facet-label">{{ facet.label }}</span>
              <span class="facet-count">{{ facet.count }}</span>
            </label>
          </div>
        </div>

        <!-- Lifecycle State Facets -->
        <div class="filter-section" *ngIf="showStateFacets">
          <div class="section-header" (click)="toggleSection('state')">
            <span>State</span>
            <span class="toggle-icon">{{ expandedSections['state'] ? '−' : '+' }}</span>
          </div>
          <div class="section-content" *ngIf="expandedSections['state']">
            <label class="facet-item" *ngFor="let facet of stateFacets">
              <input type="checkbox"
                     [checked]="selectedStates.has(facet.value)"
                     (change)="toggleState(facet.value)"
                     [disabled]="facet.count === 0" />
              <span class="facet-label">{{ facet.label }}</span>
              <span class="facet-count">{{ facet.count }}</span>
            </label>
          </div>
        </div>

        <!-- Sub Type Facets -->
        <div class="filter-section" *ngIf="showSubTypeFacets && subTypeFacets.length > 0">
          <div class="section-header" (click)="toggleSection('subType')">
            <span>Sub Type</span>
            <span class="toggle-icon">{{ expandedSections['subType'] ? '−' : '+' }}</span>
          </div>
          <div class="section-content" *ngIf="expandedSections['subType']">
            <label class="facet-item" *ngFor="let facet of subTypeFacets">
              <input type="checkbox"
                     [checked]="selectedSubTypes.has(facet.value)"
                     (change)="toggleSubType(facet.value)"
                     [disabled]="facet.count === 0" />
              <span class="facet-label">{{ facet.label }}</span>
              <span class="facet-count">{{ facet.count }}</span>
            </label>
          </div>
        </div>

        <!-- Date Range -->
        <div class="filter-section">
          <div class="section-header" (click)="toggleSection('dateRange')">
            <span>Date Range</span>
            <span class="toggle-icon">{{ expandedSections['dateRange'] ? '−' : '+' }}</span>
          </div>
          <div class="section-content" *ngIf="expandedSections['dateRange']">
            <div class="date-field">
              <label>Created After</label>
              <kendo-datepicker
                [(ngModel)]="createdAfter"
                [format]="'yyyy-MM-dd'"
                (valueChange)="onDateChange()"
                class="date-picker">
              </kendo-datepicker>
            </div>
            <div class="date-field">
              <label>Created Before</label>
              <kendo-datepicker
                [(ngModel)]="createdBefore"
                [format]="'yyyy-MM-dd'"
                (valueChange)="onDateChange()"
                class="date-picker">
              </kendo-datepicker>
            </div>
            <div class="date-presets">
              <button kendoButton [look]="'flat'" (click)="setDatePreset('today')">Today</button>
              <button kendoButton [look]="'flat'" (click)="setDatePreset('week')">Last 7 days</button>
              <button kendoButton [look]="'flat'" (click)="setDatePreset('month')">Last 30 days</button>
              <button kendoButton [look]="'flat'" (click)="setDatePreset('year')">Last year</button>
            </div>
          </div>
        </div>

        <!-- Created By -->
        <div class="filter-section">
          <div class="section-header" (click)="toggleSection('createdBy')">
            <span>Created By</span>
            <span class="toggle-icon">{{ expandedSections['createdBy'] ? '−' : '+' }}</span>
          </div>
          <div class="section-content" *ngIf="expandedSections['createdBy']">
            <input kendoTextBox
                   [(ngModel)]="createdBy"
                   placeholder="Enter username..."
                   class="text-input"
                   (blur)="onCreatedByChange()" />
          </div>
        </div>

        <!-- Additional Options -->
        <div class="filter-section">
          <div class="section-header" (click)="toggleSection('options')">
            <span>Options</span>
            <span class="toggle-icon">{{ expandedSections['options'] ? '−' : '+' }}</span>
          </div>
          <div class="section-content" *ngIf="expandedSections['options']">
            <label class="checkbox-item">
              <input type="checkbox"
                     [(ngModel)]="latestVersionOnly"
                     (change)="onOptionsChange()" />
              <span>Latest version only</span>
            </label>
          </div>
        </div>

        <!-- Apply Button (for manual apply mode) -->
        <div class="apply-section" *ngIf="!autoApply">
          <button kendoButton [themeColor]="'primary'" (click)="applyFilters()" class="apply-button">
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .search-filters-container {
      display: flex;
      flex-direction: column;
      background: var(--fv-surface-alt);
      border: 1px solid var(--fv-border);
      border-radius: 4px;
      min-width: 240px;
      font-size: 13px;
    }

    .search-filters-container.collapsed {
      min-width: auto;
    }

    .filters-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px;
      background: var(--fv-surface-hover);
      border-bottom: 1px solid var(--fv-border);
    }

    .header-title {
      font-weight: 600;
      color: var(--fv-text);
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .active-count {
      font-size: 11px;
      padding: 2px 8px;
      background: #1976d2;
      color: white;
      border-radius: 10px;
    }

    .collapse-btn {
      min-width: 28px;
      padding: 4px;
    }

    .filters-body {
      display: flex;
      flex-direction: column;
    }

    .filter-section {
      border-bottom: 1px solid var(--fv-border-subtle);
    }

    .filter-section:last-child {
      border-bottom: none;
    }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 12px;
      cursor: pointer;
      font-weight: 500;
      color: var(--fv-text);
      transition: background 0.15s;
    }

    .section-header:hover {
      background: var(--fv-border-subtle);
    }

    .toggle-icon {
      font-size: 12px;
      color: var(--fv-text-muted);
    }

    .section-content {
      padding: 8px 12px 12px;
    }

    .facet-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 0;
      cursor: pointer;
    }

    .facet-item:hover {
      background: var(--fv-surface-hover);
    }

    .facet-item input[type="checkbox"] {
      margin: 0;
    }

    .facet-item input[disabled] + .facet-label {
      color: #bbb;
    }

    .facet-label {
      flex: 1;
      color: var(--fv-text);
    }

    .facet-count {
      font-size: 11px;
      color: var(--fv-text-muted);
      background: var(--fv-border-subtle);
      padding: 1px 6px;
      border-radius: 8px;
    }

    .date-field {
      margin-bottom: 8px;
    }

    .date-field label {
      display: block;
      margin-bottom: 4px;
      font-size: 12px;
      color: var(--fv-text-secondary);
    }

    .date-picker {
      width: 100%;
    }

    .date-presets {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      margin-top: 8px;
    }

    .date-presets button {
      font-size: 11px;
      padding: 4px 8px;
    }

    .text-input {
      width: 100%;
    }

    .checkbox-item {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
    }

    .checkbox-item input[type="checkbox"] {
      margin: 0;
    }

    .apply-section {
      padding: 12px;
      border-top: 1px solid var(--fv-border);
    }

    .apply-button {
      width: 100%;
    }
  `],
  imports: [CommonModule, FormsModule, InputsModule, ButtonsModule, DropDownsModule, DateInputsModule]
})
export class SearchFiltersComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  /** Entity type facets with counts */
  @Input() entityTypeFacetCounts: Record<string, number> = {};

  /** State facets with counts */
  @Input() stateFacetCounts: Record<string, number> = {};

  /** Sub type facets with counts */
  @Input() subTypeFacetCounts: Record<string, number> = {};

  /** Show entity type facet section */
  @Input() showEntityTypeFacets = true;

  /** Show state facet section */
  @Input() showStateFacets = true;

  /** Show sub type facet section */
  @Input() showSubTypeFacets = true;

  /** Auto-apply filters on change */
  @Input() autoApply = true;

  /** Initial collapsed state */
  @Input() initialCollapsed = false;

  /** Emitted when filters change */
  @Output() filterChange = new EventEmitter<FilterChangeEvent>();

  isCollapsed = false;
  activeFiltersCount = 0;

  expandedSections: Record<string, boolean> = {
    entityType: true,
    state: true,
    subType: true,
    dateRange: false,
    createdBy: false,
    options: false
  };

  // Filter values
  selectedEntityTypes = new Set<SearchEntityType>();
  selectedStates = new Set<LifecycleState>();
  selectedSubTypes = new Set<string>();
  createdAfter: Date | null = null;
  createdBefore: Date | null = null;
  createdBy = '';
  latestVersionOnly = true;

  // Facet data
  entityTypeFacets: { value: SearchEntityType; label: string; count: number }[] = [];
  stateFacets: { value: LifecycleState; label: string; count: number }[] = [];
  subTypeFacets: { value: string; label: string; count: number }[] = [];

  ngOnInit(): void {
    this.isCollapsed = this.initialCollapsed;
    this.initializeFacets();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entityTypeFacetCounts'] || changes['stateFacetCounts'] || changes['subTypeFacetCounts']) {
      this.updateFacetCounts();
    }
  }

  private initializeFacets(): void {
    // Entity type facets
    this.entityTypeFacets = [
      { value: SearchEntityType.Part, label: 'Parts', count: 0 },
      { value: SearchEntityType.Document, label: 'Documents', count: 0 },
      { value: SearchEntityType.File, label: 'Files', count: 0 },
      { value: SearchEntityType.Change, label: 'Changes', count: 0 }
    ];

    // State facets
    this.stateFacets = [
      { value: LifecycleState.InWork, label: 'In Work', count: 0 },
      { value: LifecycleState.InReview, label: 'In Review', count: 0 },
      { value: LifecycleState.Approved, label: 'Approved', count: 0 },
      { value: LifecycleState.Released, label: 'Released', count: 0 },
      { value: LifecycleState.Superseded, label: 'Superseded', count: 0 },
      { value: LifecycleState.Obsolete, label: 'Obsolete', count: 0 }
    ];

    this.updateFacetCounts();
  }

  private updateFacetCounts(): void {
    // Update entity type counts
    this.entityTypeFacets = this.entityTypeFacets.map(f => ({
      ...f,
      count: this.entityTypeFacetCounts[this.getEntityTypeKey(f.value)] || 0
    }));

    // Update state counts
    this.stateFacets = this.stateFacets.map(f => ({
      ...f,
      count: this.stateFacetCounts[this.getStateKey(f.value)] || 0
    }));

    // Update sub type facets
    this.subTypeFacets = Object.entries(this.subTypeFacetCounts).map(([key, count]) => ({
      value: key,
      label: key,
      count
    }));
  }

  private getEntityTypeKey(type: SearchEntityType): string {
    switch (type) {
      case SearchEntityType.Part: return 'Part';
      case SearchEntityType.Document: return 'Document';
      case SearchEntityType.File: return 'File';
      case SearchEntityType.Change: return 'Change';
      default: return '';
    }
  }

  private getStateKey(state: LifecycleState): string {
    switch (state) {
      case LifecycleState.InWork: return 'InWork';
      case LifecycleState.InReview: return 'InReview';
      case LifecycleState.Approved: return 'Approved';
      case LifecycleState.Released: return 'Released';
      case LifecycleState.Superseded: return 'Superseded';
      case LifecycleState.Obsolete: return 'Obsolete';
      default: return '';
    }
  }

  toggleSection(section: string): void {
    this.expandedSections[section] = !this.expandedSections[section];
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  toggleEntityType(type: SearchEntityType): void {
    if (this.selectedEntityTypes.has(type)) {
      this.selectedEntityTypes.delete(type);
    } else {
      this.selectedEntityTypes.add(type);
    }
    this.updateActiveCount();
    if (this.autoApply) {
      this.emitFilterChange();
    }
  }

  toggleState(state: LifecycleState): void {
    if (this.selectedStates.has(state)) {
      this.selectedStates.delete(state);
    } else {
      this.selectedStates.add(state);
    }
    this.updateActiveCount();
    if (this.autoApply) {
      this.emitFilterChange();
    }
  }

  toggleSubType(subType: string): void {
    if (this.selectedSubTypes.has(subType)) {
      this.selectedSubTypes.delete(subType);
    } else {
      this.selectedSubTypes.add(subType);
    }
    this.updateActiveCount();
    if (this.autoApply) {
      this.emitFilterChange();
    }
  }

  onDateChange(): void {
    this.updateActiveCount();
    if (this.autoApply) {
      this.emitFilterChange();
    }
  }

  onCreatedByChange(): void {
    this.updateActiveCount();
    if (this.autoApply) {
      this.emitFilterChange();
    }
  }

  onOptionsChange(): void {
    if (this.autoApply) {
      this.emitFilterChange();
    }
  }

  setDatePreset(preset: string): void {
    const now = new Date();
    this.createdBefore = new Date(now);

    switch (preset) {
      case 'today':
        this.createdAfter = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        this.createdAfter = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        this.createdAfter = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'year':
        this.createdAfter = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
    }

    this.updateActiveCount();
    if (this.autoApply) {
      this.emitFilterChange();
    }
  }

  clearAllFilters(): void {
    this.selectedEntityTypes.clear();
    this.selectedStates.clear();
    this.selectedSubTypes.clear();
    this.createdAfter = null;
    this.createdBefore = null;
    this.createdBy = '';
    this.latestVersionOnly = true;
    this.updateActiveCount();
    this.emitFilterChange();
  }

  applyFilters(): void {
    this.emitFilterChange();
  }

  private updateActiveCount(): void {
    let count = 0;
    count += this.selectedEntityTypes.size;
    count += this.selectedStates.size;
    count += this.selectedSubTypes.size;
    if (this.createdAfter) count++;
    if (this.createdBefore) count++;
    if (this.createdBy) count++;
    this.activeFiltersCount = count;
  }

  private emitFilterChange(): void {
    const criteria: Partial<SearchCriteria> = {};

    // Entity type - if single selection, use it; otherwise handle multiple via backend
    if (this.selectedEntityTypes.size === 1) {
      criteria.entityType = Array.from(this.selectedEntityTypes)[0];
    }

    // State - if single selection
    if (this.selectedStates.size === 1) {
      criteria.lifecycleState = Array.from(this.selectedStates)[0];
    }

    // Sub types - use first selected part/document type
    if (this.selectedSubTypes.size > 0) {
      const firstSubType = Array.from(this.selectedSubTypes)[0];
      // Determine if it's a part type or document type based on context
      criteria.partType = firstSubType;
    }

    if (this.createdAfter) criteria.createdAfter = this.createdAfter;
    if (this.createdBefore) criteria.createdBefore = this.createdBefore;
    if (this.createdBy) criteria.createdBy = this.createdBy;
    criteria.latestVersionOnly = this.latestVersionOnly;

    this.filterChange.emit({
      criteria,
      activeFiltersCount: this.activeFiltersCount
    });
  }

  /** Public method to get current filter criteria */
  getCriteria(): Partial<SearchCriteria> {
    const criteria: Partial<SearchCriteria> = {};

    if (this.selectedEntityTypes.size === 1) {
      criteria.entityType = Array.from(this.selectedEntityTypes)[0];
    }
    if (this.selectedStates.size === 1) {
      criteria.lifecycleState = Array.from(this.selectedStates)[0];
    }
    if (this.createdAfter) criteria.createdAfter = this.createdAfter;
    if (this.createdBefore) criteria.createdBefore = this.createdBefore;
    if (this.createdBy) criteria.createdBy = this.createdBy;
    criteria.latestVersionOnly = this.latestVersionOnly;

    return criteria;
  }

  /** Public method to set filter values programmatically */
  setFilters(criteria: Partial<SearchCriteria>): void {
    this.selectedEntityTypes.clear();
    this.selectedStates.clear();

    if (criteria.entityType !== undefined) {
      this.selectedEntityTypes.add(criteria.entityType);
    }
    if (criteria.lifecycleState !== undefined) {
      this.selectedStates.add(criteria.lifecycleState);
    }
    if (criteria.createdAfter) this.createdAfter = criteria.createdAfter;
    if (criteria.createdBefore) this.createdBefore = criteria.createdBefore;
    if (criteria.createdBy) this.createdBy = criteria.createdBy;
    if (criteria.latestVersionOnly !== undefined) {
      this.latestVersionOnly = criteria.latestVersionOnly;
    }

    this.updateActiveCount();
  }
}
